/**
 * Membresías: la lista es la de las demás ofertas (`OffersPanel` con
 * `memberships`) y su ficha la de cualquier oferta; aquí viven el aviso de
 * encima de la lista y el asistente (`MembershipEditor`, que abre `MembershipWizard`).
 *
 * Las membresías (cobro mensual o anual) y los paquetes de clases
 * (pago único), cada una con las clases que incluye. Como Mindbody o Glofox:
 * se crea la membresía y se elige ahí qué incluye, en vez de ir clase por
 * clase. Por debajo es lo mismo que «Incluida con membresía» en cada reserva
 * (ver `services/bookingAccess.ts` en el worker). Desde el plan Pro.
 */
import { useEffect } from 'react'
import { IconLock } from '@tabler/icons-react'
import { Notice } from '../Notice'
import { ConfirmDialog } from '../ConfirmDialog'
import { MembershipWizard } from './MembershipWizard'
import { useSectionResource } from '../../../hooks/useSectionResource'
import { useI18n } from '../../../lib/i18n'
import { listMemberships } from '../../../lib/api'

/** El catálogo de membresías, compartido (caché de sección) por el aviso y la ventana. */
function useMembershipsCatalog(projectId: string, token: string) {
  return useSectionResource(token, [projectId, 'memberships'], () => listMemberships(projectId, token))
}

/**
 * Lo que hay que saber encima de la lista de membresías: el plan (son de Pro),
 * y la migración. Sin clases que incluir lo dice el asistente al crear. La lista es la de las demás
 * ofertas (`OffersPanel`); esto sólo pone el contexto.
 */
export function MembershipsNotices({ projectId, token, onOpenPlans }: {
  projectId: string
  token: string
  onOpenPlans?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const data = useMembershipsCatalog(projectId, token).data
  if (!data) return null
  if (!data.allowed) {
    return (
      <Notice
        tone="accent"
        icon={<IconLock size={17} />}
        title={t('svcw.membership.proTitle')}
        actions={onOpenPlans ? <button type="button" className="dash-secondary-btn" onClick={onOpenPlans}>{t('svcw.membership.seePlans')}</button> : undefined}
      >
        <p>{t('memberships.proBody')}</p>
      </Notice>
    )
  }
  if (!data.ready) return <Notice title={t('svcw.membership.notReady')}><p>{t('memberships.subtitle')}</p></Notice>
  return null
}

/**
 * El asistente de una membresía: la misma ventana para crearla (desde la lista)
 * y para editarla (desde «Ajustes» de su ficha), como el asistente de las demás
 * ofertas. Sin plan Pro no se abre: lleva a los planes.
 */
export function MembershipEditor({ projectId, token, offerId, onClose, onSaved, onOpenPlans, onOpenCatalog }: {
  projectId: string
  token: string
  /** Nulo = membresía nueva. */
  offerId: string | null
  onClose: () => void
  onSaved: (offerId: string) => void
  onOpenPlans?: (() => void) | undefined
  /** Lleva al catálogo, donde se crean las ofertas que una membresía incluye o descuenta. */
  onOpenCatalog?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const catalog = useMembershipsCatalog(projectId, token)
  const data = catalog.data
  const blocked = Boolean(data && !data.allowed)
  useEffect(() => {
    if (!blocked) return
    onClose()
    onOpenPlans?.()
  }, [blocked, onClose, onOpenPlans])
  if (!data || blocked) return null
  const item = offerId ? data.items.find((row) => row.offerId === offerId) ?? null : null
  /* Una que todavía no está en la copia guardada: se espera a la consulta. */
  if (offerId && !item) return null
  /* Una membresía da algo: clases con reserva incluidas o descuento en el
     resto del catálogo. Sin ninguna oferta no daría nada; se dice al ir a
     crearla, en su ventana. Con cualquiera de las dos, adelante. */
  if (!offerId && data.bookings.length === 0 && (data.discountables ?? []).length === 0) {
    return (
      <ConfirmDialog
        title={t('memberships.needBookingsTitle')}
        message={t('memberships.needBookingsBody')}
        confirmLabel={t('memberships.needBookingsCta')}
        onCancel={onClose}
        onConfirm={() => { onClose(); onOpenCatalog?.() }}
      />
    )
  }
  return (
    <MembershipWizard
      projectId={projectId}
      token={token}
      catalog={data}
      item={item}
      onClose={onClose}
      onSaved={(savedId) => { void catalog.refresh(); onSaved(savedId) }}
    />
  )
}
